import { useState, useEffect, useRef } from 'react';
import { Bell, UserPlus, DollarSign, CheckCircle, X, ChevronRight, FileText, MessageSquare, Wrench } from 'lucide-react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import { useTheme } from './ThemeContext';

// Format relative time (e.g., "2 minutes ago", "1 hour ago")
const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
};

// Get icon for notification type
const getNotificationIcon = (type) => {
  switch (type) {
    case 'customer_signup':
      return <UserPlus size={16} />;
    case 'payment_received':
      return <DollarSign size={16} />;
    case 'job_completed':
      return <CheckCircle size={16} />;
    case 'quote_signed':
      return <FileText size={16} />;
    case 'sms_received':
      return <MessageSquare size={16} />;
    case 'job_assigned':
      return <Wrench size={16} />;
    default:
      return <Bell size={16} />;
  }
};

// Get icon background color
const getIconColor = (type) => {
  switch (type) {
    case 'customer_signup':
      return '#10b981'; // green
    case 'payment_received':
      return '#3b82f6'; // blue
    case 'job_completed':
      return '#8b5cf6'; // purple
    case 'quote_signed':
      return '#f59e0b'; // amber/orange
    case 'sms_received':
      return '#06b6d4'; // cyan
    case 'job_assigned':
      return '#f97316'; // orange
    default:
      return '#6b7280'; // gray
  }
};

export default function NotificationBell({ companyId, userId, onNavigate }) {
  const { colors } = useTheme();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);

  // Fetch notifications on mount
  useEffect(() => {
    if (!companyId) return;

    fetchNotifications();

    // Subscribe to realtime updates
    const channel = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `company_id=eq.${companyId}`
        },
        (payload) => {
          // Only add if it's for this user or all users
          if (!payload.new.user_id || payload.new.user_id === userId) {
            setNotifications(prev => [payload.new, ...prev]);
            if (!payload.new.read) {
              setUnreadCount(prev => prev + 1);
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [companyId, userId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const fetchNotifications = async () => {
    try {
      const { data, error } = await supabaseData
        .from('notifications')
        .select('*')
        .eq('company_id', companyId)
        .or(`user_id.is.null,user_id.eq.${userId}`)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;

      setNotifications(data || []);
      setUnreadCount((data || []).filter(n => !n.read).length);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await supabaseData
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId);

      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
    }
  };

  const markAllAsRead = async () => {
    try {
      const unreadIds = notifications.filter(n => !n.read).map(n => n.id);
      if (unreadIds.length === 0) return;

      await supabaseData
        .from('notifications')
        .update({ read: true })
        .in('id', unreadIds);

      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
    }
  };

  const handleNotificationClick = (notification) => {
    // Mark as read
    if (!notification.read) {
      markAsRead(notification.id);
    }

    // Navigate based on type
    if (notification.type === 'customer_signup' && onNavigate) {
      onNavigate('customers', { customerId: notification.data?.customer_id });
    } else if (notification.type === 'payment_received' && onNavigate) {
      onNavigate('work-orders', { invoiceId: notification.data?.invoice_id });
    } else if (notification.type === 'job_completed' && onNavigate) {
      onNavigate('work-orders', { jobId: notification.data?.job_id });
    } else if (notification.type === 'quote_signed' && onNavigate) {
      onNavigate('work-orders', { quoteId: notification.data?.quote_id });
    } else if (notification.type === 'sms_received' && onNavigate) {
      onNavigate('customers', { customerId: notification.data?.customer_id });
    } else if (notification.type === 'job_assigned' && onNavigate) {
      onNavigate('work-orders', { jobId: notification.data?.job_id });
    }

    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          background: 'none',
          border: 'none',
          padding: '0.5rem',
          cursor: 'pointer',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Bell size={22} color={colors.text} />

        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute',
            top: '2px',
            right: '2px',
            background: '#ef4444',
            color: 'white',
            fontSize: '0.65rem',
            fontWeight: 'bold',
            minWidth: '16px',
            height: '16px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 4px'
          }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          right: '0',
          marginTop: '0.5rem',
          width: '320px',
          maxWidth: 'calc(100vw - 2rem)',
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
          zIndex: 9999,
          overflow: 'hidden'
        }}>
          {/* Header */}
          <div style={{
            padding: '0.75rem 1rem',
            borderBottom: `1px solid ${colors.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ fontWeight: 'bold', color: colors.text }}>
              Notifications
            </span>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                style={{
                  background: 'none',
                  border: 'none',
                  color: colors.accent,
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                  padding: '0.25rem 0.5rem'
                }}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Notification List */}
          <div style={{
            maxHeight: '400px',
            overflowY: 'auto'
          }}>
            {loading ? (
              <div style={{
                padding: '2rem',
                textAlign: 'center',
                color: colors.textMuted
              }}>
                Loading...
              </div>
            ) : notifications.length === 0 ? (
              <div style={{
                padding: '2rem',
                textAlign: 'center',
                color: colors.textMuted
              }}>
                <Bell size={32} style={{ opacity: 0.3, marginBottom: '0.5rem' }} />
                <div>No notifications yet</div>
              </div>
            ) : (
              notifications.map(notification => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  style={{
                    padding: '0.75rem 1rem',
                    borderBottom: `1px solid ${colors.border}`,
                    cursor: 'pointer',
                    display: 'flex',
                    gap: '0.75rem',
                    alignItems: 'flex-start',
                    background: notification.read ? 'transparent' : `${colors.accent}10`,
                    transition: 'background 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.background = colors.bgInput}
                  onMouseOut={(e) => e.currentTarget.style.background = notification.read ? 'transparent' : `${colors.accent}10`}
                >
                  {/* Icon */}
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    background: getIconColor(notification.type),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    flexShrink: 0
                  }}>
                    {getNotificationIcon(notification.type)}
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontWeight: notification.read ? 'normal' : 'bold',
                      color: colors.text,
                      fontSize: '0.9rem',
                      marginBottom: '0.25rem'
                    }}>
                      {notification.title}
                    </div>
                    {notification.message && (
                      <div style={{
                        color: colors.textMuted,
                        fontSize: '0.8rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {notification.message}
                      </div>
                    )}
                    <div style={{
                      color: colors.textMuted,
                      fontSize: '0.75rem',
                      marginTop: '0.25rem'
                    }}>
                      {formatTimeAgo(notification.created_at)}
                    </div>
                  </div>

                  {/* Unread indicator */}
                  {!notification.read && (
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '4px',
                      background: colors.accent,
                      flexShrink: 0,
                      marginTop: '0.25rem'
                    }} />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
